import SearchIcon from '@material-ui/icons/Search'
import React, { ChangeEvent, forwardRef, useEffect, useState } from 'react'
import { HandlerOf } from 'shared/helpers/typeHelper'
import styled, { css } from 'styled-components'

import { TextInput, TextInputProps } from '../TextInput/TextInput'
import { InputStyled } from '../TextInput/TextInput.styles'
import { DropdownProps } from './Dropdown'
import { DropdownContainer } from './Dropdown.styles'
import { DropDownOpener } from './DropdownOpener'
import { VirtualDropDownOpener } from './VirtualDropdownOpener'

export interface SearchDropdownProps
  extends Omit<DropdownProps<string>, 'renderOpener' | 'staticLabel' | 'onChange' | 'error'> {
  error?: React.ReactElement | string | boolean
  placeholder?: string
  inputLabel?: string
  disableOptionFilter?: boolean
  onChange?: HandlerOf<string>
  maxHeight: number
  width: number | string
  itemSize?: number
  inputProps?: TextInputProps
  rightAmendment?: React.ReactNode
  disableVirtualDropdown?: boolean
  clearOnBlur?: boolean
  name?: string
}

const StyledRightAmendment = styled.div`
  position: absolute;
  top: 1px;
  right: 1px;
  bottom: 1px;
  border-radius: 0 3px 3px 0;
`

const StyledTextInput = styled(TextInput)<{ $hideFocusBorder?: boolean }>`
  ${(p) =>
    p.$hideFocusBorder &&
    css`
      ${InputStyled}:focus {
        border-bottom-color: transparent;
      }
    `}
`

export const SearchDropdown = forwardRef(function SearchDropdown(props: SearchDropdownProps, ref: any) {
  const {
    name,
    options,
    inputLabel,
    value,
    className,
    error,
    warning,
    isFullWidth,
    containerMaxWidth,
    dataTestId,
    onChange,
    disableOptionFilter,
    placeholder,
    emptyOptionsLabel,
    inputProps,
    rightAmendment,
    onToggle,
    disableVirtualDropdown,
    clearOnBlur,
  } = props
  const [currentOptions, setCurrentOptions] = useState(options)
  const [currentValue, setCurrentValue] = useState<string>(value || '')
  const usedOptions = disableOptionFilter ? props.options : currentOptions

  function handleOptionChange(value: string) {
    setCurrentValue(value)
    onChange?.(value)
  }

  function handleTextChange(e: ChangeEvent<HTMLInputElement>) {
    setCurrentValue(e.target.value)
    onChange?.(e.target.value)
  }

  function handleTextClick(setOpen: HandlerOf<boolean>) {
    return (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      onToggle?.(true)
      setOpen(true)
    }
  }

  useEffect(() => {
    if (disableOptionFilter) return
    if (currentValue || currentValue === '') {
      setCurrentOptions(
        options.filter((o) => String(o.value)?.toLocaleLowerCase()?.match(currentValue.toLocaleLowerCase()))
      )
    } else {
      setCurrentOptions(options)
    }
  }, [options, currentValue, disableOptionFilter])

  useEffect(() => {
    if (props.value || props.value === '') setCurrentValue(props.value)
  }, [props.value])

  const Opener = disableVirtualDropdown ? DropDownOpener : VirtualDropDownOpener

  return (
    <DropdownContainer
      className={className}
      $noMargin
      $isFullWidth={isFullWidth}
      $minWidth="0"
      $maxWidth={containerMaxWidth}
    >
      <Opener
        {...props}
        className="search-dropdown-opener"
        emptyOptionsLabel={emptyOptionsLabel}
        options={usedOptions}
        onChange={handleOptionChange}
        error={Boolean(error)}
        ref={ref}
        onNonFormBlur={() => {
          if (clearOnBlur) {
            onChange?.('')
          }
        }}
      >
        {(isOpen, setOpen) => (
          <>
            <StyledTextInput
              name={name}
              className="search-dropdown-input"
              label={inputLabel}
              placeholder={placeholder}
              value={currentValue}
              error={error}
              warning={warning}
              onChange={handleTextChange}
              onClick={handleTextClick(setOpen)}
              dataTestid={dataTestId || 'search-dropdown'}
              endAdornment={!rightAmendment && <SearchIcon />}
              $hideFocusBorder={Boolean(emptyOptionsLabel) || usedOptions?.length !== 0}
              noMargin
              {...inputProps}
            />
            {!isOpen && rightAmendment && (
              <StyledRightAmendment className="search-dropdown-right-amendment">
                {rightAmendment}
              </StyledRightAmendment>
            )}
          </>
        )}
      </Opener>
    </DropdownContainer>
  )
})
